/**
 * Payment form fields grid: date, amount, method, bank account, reference, exchange rate, notes.
 */

import { Input, FormField } from '@elsapiens/ui'
import { Link } from 'react-router-dom'

import { PAYMENT_METHOD_OPTIONS } from '../../services/payment-service'
import { DatePicker, CurrencyInput, Select } from '../ui'

interface RecordPaymentFormFieldsProps {
  paymentDate: Date
  setPaymentDate: (d: Date) => void
  paymentAmount: number | null
  setPaymentAmount: (v: number | null) => void
  paymentMethod: string
  setPaymentMethod: (v: string) => void
  selectedBankAccountId: string
  setSelectedBankAccountId: (v: string) => void
  paymentAccountOptions: { value: string; label: string }[]
  paymentRef: string
  setPaymentRef: (v: string) => void
  paymentNotes: string
  setPaymentNotes: (v: string) => void
  invoiceCurrency: string
  baseCurrency: string
  isForeignCurrency: boolean
  isExchangeRateLoading: boolean
  paymentExchangeRate: string
  setPaymentExchangeRate: (v: string) => void
}

function getBankAccountLabel(paymentMethod: string): string {
  if (paymentMethod === 'PAYMENT_METHOD_CASH') { return 'Cash Account' }
  if (paymentMethod === 'PAYMENT_METHOD_CREDIT_CARD') { return 'Credit Card' }
  return 'Bank Account'
}

interface BankAccountFieldProps {
  label: string
  options: { value: string; label: string }[]
  value: string
  onChange: (v: string) => void
}

function BankAccountField({ label, options, value, onChange }: BankAccountFieldProps): JSX.Element {
  if (options.length > 0) {
    return (
      <FormField label={label}>
        <Select hideMessage options={options.map(o => ({ value: o.value, label: o.label }))} size="sm" value={value} onChange={onChange} />
      </FormField>
    )
  }
  return (
    <FormField label={label}>
      <p className="text-xs text-foreground-secondary italic el-py-xs">
        No bank or cash accounts configured.
        <Link className="text-accent ml-1 hover:underline" to="/settings">Add one</Link>
      </p>
    </FormField>
  )
}

export function RecordPaymentFormFields({
  paymentDate, setPaymentDate, paymentAmount, setPaymentAmount,
  paymentMethod, setPaymentMethod, selectedBankAccountId, setSelectedBankAccountId,
  paymentAccountOptions, paymentRef, setPaymentRef, paymentNotes, setPaymentNotes,
  invoiceCurrency, baseCurrency, isForeignCurrency, isExchangeRateLoading,
  paymentExchangeRate, setPaymentExchangeRate,
}: RecordPaymentFormFieldsProps): JSX.Element {
  const wideStyle = { marginInline: 'calc(var(--el-container-px) * -0.5)', width: 'calc(100% + var(--el-container-px))' }
  return (
    <div className="el-p-md" style={wideStyle}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 el-gap-field">
        <FormField label="Payment Date">
          <DatePicker value={paymentDate} onChange={d => d && setPaymentDate(d)} />
        </FormField>
        <FormField label="Amount">
          <CurrencyInput hideMessage currency={invoiceCurrency} size="sm" value={paymentAmount} onChange={setPaymentAmount} />
        </FormField>
        <FormField label="Method">
          <Select hideMessage options={PAYMENT_METHOD_OPTIONS} size="sm" value={paymentMethod} onChange={value => setPaymentMethod(value)} />
        </FormField>
        <BankAccountField label={getBankAccountLabel(paymentMethod)} options={paymentAccountOptions} value={selectedBankAccountId} onChange={setSelectedBankAccountId} />
        <FormField label="Reference #">
          <Input hideMessage placeholder="Ref / Cheque #" size="sm" value={paymentRef} onChange={e => setPaymentRef(e.target.value)} />
        </FormField>
        {isForeignCurrency && (
          <FormField helperText={isExchangeRateLoading ? 'Fetching rate...' : undefined} label={`Exchange Rate (${invoiceCurrency}/${baseCurrency})`}>
            <Input hideMessage placeholder={isExchangeRateLoading ? 'Loading...' : '0.00'} size="sm" step="any" type="number" value={paymentExchangeRate} onChange={e => setPaymentExchangeRate(e.target.value)} />
          </FormField>
        )}
        <FormField label="Notes">
          <Input hideMessage placeholder="Payment notes" size="sm" value={paymentNotes} onChange={e => setPaymentNotes(e.target.value)} />
        </FormField>
      </div>
    </div>
  )
}
